import DropdownButton from 'ext-ui/components/DropdownButton/DropdownButton'
import { Moment } from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DateRangePresetMenu, {
  DateRangePresetOption,
} from 'src/components/DateRangePresetMenu/DateRangePresetMenu'
import SvgCalendarNew from 'src/components/Svgs/SvgCalendarNew'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import { useTranslate } from 'src/localization/Trns'

import * as style from '../Reporting.style'
import {
  DEFAULT_REPORTING_PRESET,
  formatReportingDateRange,
  ReportingDateRange,
  ReportingPreset,
  reportingPresetLabelKey,
  reportingPresetOrder,
  resolveReportingPresetWindow,
} from './reportingPanelUtils'

interface ReportingDateRangeControlProps {
  dateRange: ReportingDateRange
  /**
   * Presets/reset emit a full range. Edits to an already-active custom range
   * emit partial updates; edits that start from a preset stay local until both
   * endpoints are known so preset dates do not leak into the custom draft.
   */
  onChange: (range: Partial<ReportingDateRange>) => void
  testId?: string
}

type CustomDraft = Partial<Pick<ReportingDateRange, 'since' | 'until'>>

/**
 * The reporting dashboard's single, page-level date selector. Per the design
 * spec it sits in the page toolbar (next to the currency toggle) and drives the
 * range for every chart at once. The trigger is the DS {@link DropdownButton}
 * (`variant="secondary"`) and the dropdown reuses the shared
 * {@link DateRangePresetMenu} component (the same UI widget as the
 * transactions/accounting filter), though the date math here is UTC while that
 * filter is browser-local.
 */
const ReportingDateRangeControl: React.FC<ReportingDateRangeControlProps> = ({
  dateRange,
  onChange,
  testId = 'reporting-date-range',
}) => {
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const customDraftRef = useRef<CustomDraft>({})
  const [customDraft, setCustomDraft] = useState<CustomDraft>({})

  const updateCustomDraft = useCallback((draft: CustomDraft) => {
    customDraftRef.current = draft
    setCustomDraft(draft)
  }, [])

  const clearCustomDraft = useCallback(() => {
    updateCustomDraft({})
  }, [updateCustomDraft])

  useEffect(() => {
    if (!open && dateRange.preset !== 'custom') {
      clearCustomDraft()
    }
  }, [clearCustomDraft, dateRange.preset, open])

  const options = useMemo<DateRangePresetOption[]>(
    () =>
      reportingPresetOrder.map((preset) => {
        const range = resolveReportingPresetWindow(preset)
        return {
          key: preset,
          label: translate(reportingPresetLabelKey[preset]),
          subLabel: formatReportingDateRange(range),
          isActive: preset === dateRange.preset,
          testId: `${testId}-option-${preset}`,
        }
      }),
    [dateRange.preset, testId, translate],
  )

  const handleSelectOption = useCallback(
    (key: string) => {
      clearCustomDraft()
      setOpen(false)
      onChange(resolveReportingPresetWindow(key as ReportingPreset))
    },
    [clearCustomDraft, onChange],
  )

  const handleReset = useCallback(() => {
    clearCustomDraft()
    setOpen(false)
    onChange(resolveReportingPresetWindow(DEFAULT_REPORTING_PRESET))
  }, [clearCustomDraft, onChange])

  // Active custom edits can emit partials because the parent already owns a
  // custom range. Preset-to-custom edits stay local until complete so the
  // parent's merge cannot retain an old preset endpoint.
  const handleCustomSince = useCallback(
    (since: Moment | undefined) => {
      if (since === undefined) return
      if (dateRange.preset === 'custom') {
        onChange({ since, preset: 'custom' })
        return
      }

      const draft = { ...customDraftRef.current, since }
      updateCustomDraft(draft)
      if (draft.since && draft.until) {
        onChange({ ...draft, preset: 'custom' })
        setOpen(false)
      }
    },
    [dateRange.preset, onChange, updateCustomDraft],
  )

  const handleCustomUntil = useCallback(
    (until: Moment | undefined) => {
      if (until === undefined) return
      if (dateRange.preset === 'custom') {
        onChange({ until, preset: 'custom' })
        setOpen(false)
        return
      }

      const draft = { ...customDraftRef.current, until }
      updateCustomDraft(draft)
      if (draft.since && draft.until) {
        onChange({ ...draft, preset: 'custom' })
        setOpen(false)
      }
    },
    [dateRange.preset, onChange, updateCustomDraft],
  )

  const customSince =
    dateRange.preset === 'custom' ? dateRange.since : customDraft.since
  const customUntil =
    dateRange.preset === 'custom' ? dateRange.until : customDraft.until
  const hasCustomDraft =
    dateRange.preset !== 'custom' &&
    (customDraft.since !== undefined || customDraft.until !== undefined)

  const activeLabel =
    dateRange.preset === 'custom'
      ? formatReportingDateRange(dateRange)
      : translate(reportingPresetLabelKey[dateRange.preset])

  return (
    <span css={style.dateRangeControl}>
      <DropdownButton
        variant="secondary"
        data-testid={testId}
        data-active-preset={dateRange.preset}
        isOpen={open}
        onToggle={setOpen}
        // The shared menu stops click propagation on its rows, so we close the
        // dropdown explicitly from each handler rather than relying on this.
        closeOnSelect={false}
        // Trigger sits at the right of the page header, so anchor the menu's right
        // edge to it and let it extend left — keeps it on-screen.
        placement="bottom-end"
        iconLeft={<SvgCalendarNew width={16} height={16} aria-hidden="true" />}
        iconRight={
          open ? (
            <SvgChevronUp
              width={16}
              height={16}
              viewBox="0 0 24 24"
              aria-hidden="true"
            />
          ) : (
            <SvgChevronDown
              width={16}
              height={16}
              viewBox="0 0 24 24"
              aria-hidden="true"
            />
          )
        }
        menu={
          <DateRangePresetMenu
            options={options}
            onSelectOption={handleSelectOption}
            custom={{
              label: translate('Custom Date Range'),
              isActive: dateRange.preset === 'custom',
              seedInactiveRange: hasCustomDraft,
              activeSubLabel: formatReportingDateRange(dateRange),
              since: customSince,
              until: customUntil,
              setSince: handleCustomSince,
              setUntil: handleCustomUntil,
              utc: true,
            }}
            backLabel={translate('Back')}
            resetLabel={translate('Reset to ${label}', {
              label: translate(
                reportingPresetLabelKey[DEFAULT_REPORTING_PRESET],
              ),
            })}
            onReset={handleReset}
            parentOpen={open}
            menuTestId={`${testId}-menu`}
            resetTestId={`${testId}-reset`}
          />
        }
      >
        {activeLabel}
      </DropdownButton>
    </span>
  )
}

export default ReportingDateRangeControl
